import { Color as DeckColor, Layer } from '@deck.gl/core';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { logger } from '@fe/core';
import allPlanesPng from '../../img/all-planes-map-sdf.png';
import allPlanesJson from '../../img/all-planes-map.json';
import { IFlightItem } from '../flights/types';
import { iconPixelSize, IconType, SubstituteIconType } from '../IconType';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { AnimatedFlightLayer } from './layers/flight/animated-flight-layer';
import { FlightIconLayerProps, FlightsIconLayer } from './layers/flight/flight-icon-layer';
import { AnimatedLineLayer } from './layers/line/animated-line-layer';
import { AnimatedScatterplotLayer } from './layers/scatterplot/animated-scatterplot-layer';
import { HidableScatterplotLayer } from './layers/scatterplot/hidable-scatterplot-layer';

export class DeckActiveFlights extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    createLayers(data: IFlightItem[]): Layer[] {
        if (!this.props?.flights.active.visible && !this.props?.flights.groundVehicles.visible) {
            return [];
        }
        const { showShadow, iconZoomThreshold, substituteIconType, showProjectedPoints } = this.props.flights.active;

        const flightLayerProps: FlightIconLayerProps<IFlightItem> = {
            id: this.isCoastMode ? 'animated-flights' : 'flights',
            data,
            sdfParams: this.plugin.planeSdfParams,
            isGlobeView: this.useGlobeView,
            outlineColor: [0, 0, 0, 0.5],
            shadowColor: [0, 0, 0, 0.2],
            showShadow: showShadow,
            billboard: false,
            sizeUnits: 'pixels',
            sizeScale: this.getIconScale(),
            getColor: (d) => this.getPlaneColor(d),
            getSize: (d) => iconPixelSize(d.iconType),
            getAngle: (d) => this.getFlightHeading(d) ?? 0,
            getIsHidden: (d) => this.getIsHidden(d),
            getIcon: (d) =>
                // No heading -> circle
                (this.getFlightHeading(d) === null || this.zoom <= iconZoomThreshold
                    ? IconType.circle
                    : substituteIconType === SubstituteIconType.auto
                      ? d.iconType
                      : substituteIconType
                ).toString(),
            iconMapping: allPlanesJson,
            iconAtlas: allPlanesPng,
            pickable: true,
            onClick: this.plugin.selection.onFlightSelected,
            updateTriggers: {
                all: [this.flightsVersion],
                getColor: [this.selectionVersion],
                getAngle: [this.propsVersion],
                getIsHidden: this.getIsHiddenUpdateTrigger,
                getIcon: [this.zoom, iconZoomThreshold, substituteIconType]
            }
        };

        const layers: Layer[] = [
            this.isCoastMode
                ? new AnimatedFlightLayer<IFlightItem>({
                      ...flightLayerProps,
                      getId: (x) => x.id,
                      positionTexture: this.plugin.positionTexture!
                  })
                : new FlightsIconLayer<IFlightItem>({
                      ...flightLayerProps,
                      getPosition: (d) => this.getFlightPosition(d),
                      updateTriggers: {
                          ...flightLayerProps.updateTriggers,
                          getPosition: [showProjectedPoints]
                      }
                  })
        ];

        if (this.props.flights.active.followedFlightId !== null) {
            const flight = this.plugin.flightsUpdater.getFlight(this.props.flights.active.followedFlightId);
            if (flight && flight.id === this.props.flights.active.followedFlightId && !this.getIsHidden(flight)) {
                layers.push(
                    this.props.flights.active.isCoastMode
                        ? new AnimatedScatterplotLayer({
                              id: 'animated-followed-flight-markers',
                              data: [flight],
                              positionTexture: this.plugin.positionTexture,
                              getId: (d) => d.id,
                              getRadius: (d) => iconPixelSize(d.iconType) / 2 + 10,
                              radiusScale: this.getIconScale(),
                              radiusUnits: 'pixels',
                              getLineColor: [0x70, 0x4d, 0xff, 255],
                              getFillColor: [0x70, 0x4d, 0xff, 25],
                              stroked: true,
                              getLineWidth: 1,
                              lineWidthUnits: 'pixels',
                              updateTriggers: {
                                  getPosition: [showProjectedPoints],
                                  getLineColor: [this.zoom, iconZoomThreshold, substituteIconType]
                              }
                          })
                        : new ScatterplotLayer({
                              id: 'followed-flight-markers',
                              data: [flight],
                              getPosition: (d) => this.getFlightPosition(d),
                              getRadius: (d) => iconPixelSize(d.iconType) / 2 + 10,
                              radiusScale: this.getIconScale(),
                              radiusUnits: 'pixels',
                              getLineColor: [0x70, 0x4d, 0xff, 255],
                              getFillColor: [0x70, 0x4d, 0xff, 25],
                              stroked: true,
                              getLineWidth: 1,
                              lineWidthUnits: 'pixels',
                              updateTriggers: {
                                  getPosition: [showProjectedPoints],
                                  getLineColor: [this.zoom, iconZoomThreshold, substituteIconType]
                              }
                          })
                );
            } else {
                logger.warn('FlightLayer: Followed flight not found');
            }
        }

        if (this.props.flights.active.isCoastMode && this.props.flights.active.coastDebugOverlay) {
            layers.push(
                /*new AnimatedLineLayer({
                    id: 'coast-debug-from-last-to-projected',
                    data,
                    positionTexture: this.plugin.positionTexture,
                    getTargetPosition: (d) => this.getFlightPosition(d),
                    getId: (d) => d.id,
                    getIsHidden: (d) => this.getIsHidden(d),
                    getWidth: 2,
                    widthUnits: 'pixels',
                    getColor: [0, 255, 0, 255],
                    updateTriggers: {
                        getTargetPosition: this.getPositionUpdateTrigger,
                        getIsHidden: this.getIsHiddenUpdateTrigger,
                        all: [this.flightsVersion]
                    }
                }),
                new AnimatedLineLayer({
                    id: 'coast-debug-current-to-from',
                    data: data,
                    positionTexture: this.plugin.positionTexture,
                    getId: (d) => d.id,
                    getTargetPosition: (d) => d.coastFromPosition ?? [0, 0],
                    getIsHidden: (d) => this.getIsHidden(d) || (d.coastFromPosition == null ? 1 : 0),
                    getWidth: 2,
                    widthUnits: 'pixels',
                    getColor: [255, 0, 0, 255],
                    updateTriggers: {
                        getTargetPosition: this.getPositionUpdateTrigger,
                        getIsHidden: this.getIsHiddenUpdateTrigger,
                        all: [this.flightsVersion]
                    }
                }),

                new HidableLineLayer<IFlightItem>({
                    id: 'coast-debug-from-to',
                    data,
                    getSourcePosition: (d) => d.coastFromPosition ?? [0, 0],
                    getTargetPosition: (d) => d.coastToPosition ?? [0, 0],
                    getIsHidden: (d) => this.getIsHidden(d) || (d.coastFromPosition == null || d.coastToPosition == null ? 1 : 0),
                    getWidth: 2,
                    widthUnits: 'pixels',
                    getColor: [0, 0, 255, 127],
                    updateTriggers: {
                        getTargetPosition: this.getPositionUpdateTrigger,
                        getIsHidden: this.getIsHiddenUpdateTrigger,
                        all: [this.flightsVersion]
                    }
                })*/
                new HidableScatterplotLayer<IFlightItem>({
                    id: 'coast-to-position',
                    data,
                    getPosition: (d) => d.coastToPosition ?? [0, 0],
                    getIsHidden: (d) => (this.getIsHidden(d) || d.coastToPosition == null ? 1 : 0),
                    getRadius: 2,
                    radiusUnits: 'pixels',
                    getFillColor: [0, 0, 255, 127],
                    updateTriggers: {
                        getTargetPosition: this.getPositionUpdateTrigger,
                        getIsHidden: this.getIsHiddenUpdateTrigger,
                        all: [this.flightsVersion]
                    }
                }),
                new AnimatedLineLayer({
                    id: 'coast-debug-current-to-to',
                    data: data,
                    positionTexture: this.plugin.positionTexture,
                    getId: (d) => d.id,
                    getTargetPosition: (d) => d.coastToPosition ?? [0, 0],
                    getIsHidden: (d) => this.getIsHidden(d) || (d.coastToPosition == null ? 1 : 0),
                    getWidth: 2,
                    widthUnits: 'pixels',
                    getColor: [255, 0, 0, 255],
                    updateTriggers: {
                        getTargetPosition: this.getPositionUpdateTrigger,
                        getIsHidden: this.getIsHiddenUpdateTrigger,
                        all: [this.flightsVersion]
                    }
                }),
                new TextLayer<IFlightItem>({
                    id: 'coast-to-text',
                    data,
                    getPosition: (d) => d.coastToPosition ?? [0, 0],
                    getText: (d) => (d.coastToTime ? d.coastToTime - this.plugin.flightsUpdater.coastOffset : null)?.toFixed(3) ?? '',
                    getSize: 12,
                    getPixelOffset: [20, 20],
                    updateTriggers: {
                        all: [this.flightsVersion]
                    }
                }),
                new ScatterplotLayer<IFlightItem>({
                    id: 'coast-real-points',
                    data,
                    getPosition: (d) => this.getFlightPosition(d),
                    getRadius: 2,
                    radiusUnits: 'pixels',
                    getColor: [255, 0, 0, 255],
                    updateTriggers: {
                        all: [this.flightsVersion]
                    }
                })
            );
        }

        return layers;
    }

    private getPlaneColor(item: IFlightItem): DeckColor {
        const { highlightColor, defaultFlightColor } = this.props!.flights.active;
        return this.plugin.selection.isSelected(item.id)
            ? highlightColor
            : (item.alertState?.color ?? item.filterColor ?? defaultFlightColor);
    }
}
